import type { TilePosition } from '@shefira/shared';

import { isAction, type KeyAction, type KeyPress } from '../settings/keybindings.js';

/**
 * Walking with the keys (the arrows by default), beside the click.
 *
 * Only an intention, sent through the scene's `sendWalk` (ADR 0045) and
 * walked by the server. A held key asks, in one `move_to`, for the farthest
 * open tile straight ahead (at most `TRIP_TILES`), like a click on the
 * ground; a few tiles before the end, the trip is asked again from where the
 * walk turns, so the body never stops and the server always knows the path
 * ahead instead of learning each step half a round trip late. A message
 * every few steps, far under the session's budget (ADR 0040).
 *
 * Letting the key go asks for one tile more and no farther: the walk ends on
 * the next tile, never back. Another key starts a new trip at once. An
 * obstacle ends the trip on the tile before it, with no way around.
 *
 * The grid is four-connected (the server's A* walks no diagonal), so two
 * arrows held at once do not make a diagonal: the last one pressed leads,
 * and letting it go hands the walk back to the one still held.
 */

export type WalkDirection = 'up' | 'down' | 'left' | 'right';

const WALK_ACTIONS: readonly (readonly [WalkDirection, KeyAction])[] = [
  ['up', 'walkUp'],
  ['down', 'walkDown'],
  ['left', 'walkLeft'],
  ['right', 'walkRight'],
];

/** The farthest a trip goes straight ahead. */
export const TRIP_TILES = 8;
/** A trip with this few tiles left is asked again, farther. */
export const EXTEND_AT_STEPS = 3;

const STEPS: Readonly<Record<WalkDirection, TilePosition>> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

/** The walking direction a key press is bound to, if any. */
export function walkDirectionOf(event: KeyPress): WalkDirection | undefined {
  return WALK_ACTIONS.find(([, action]) => isAction(event, action))?.[0];
}

/**
 * A field that takes the arrows for itself: the chat's line, a slider of the
 * settings, a list. The player is typing there, not walking.
 */
export function isTypingTarget(element: Element | null): boolean {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    (element instanceof HTMLElement && element.isContentEditable)
  );
}

export class KeyboardWalk {
  /**
   * Keys held, by position (`event.code`): a key is let go by the same
   * position, even with a modifier pressed meanwhile or a binding changed.
   * In pressing order, the latest last.
   */
  private held: { code: string; direction: WalkDirection }[] = [];

  /** The last trip asked: its direction, and its end if it went out. */
  private trip: { direction: WalkDirection; target: TilePosition | undefined } | undefined;

  /** A walking key goes down; true when it is a new press, not the key's repeat. */
  press(code: string, direction: WalkDirection): boolean {
    if (this.held.some((key) => key.code === code)) return false;
    this.held.push({ code, direction });
    return true;
  }

  release(code: string): void {
    this.held = this.held.filter((key) => key.code !== code);
  }

  /** The window lost the focus: its key-ups will never come. */
  releaseAll(): void {
    this.held = [];
  }

  get direction(): WalkDirection | undefined {
    return this.held.at(-1)?.direction;
  }

  /**
   * One frame: asks through `send` what the held keys want of the walk, and
   * returns the tile asked, if any. `send` says whether the walk went out.
   */
  follow(
    walk: WalkView,
    isWalkable: (tile: TilePosition) => boolean,
    send: (tile: TilePosition) => boolean,
  ): TilePosition | undefined {
    const trip = this.trip;
    // The walk under way is the last trip asked, not a click's or the server's.
    const own =
      trip?.target !== undefined && walk.target !== undefined && same(walk.target, trip.target)
        ? { direction: trip.direction, target: trip.target }
        : undefined;
    // Ends the walk under way on the next tile, if it was going farther.
    const halt = (): TilePosition | undefined => {
      if (own === undefined || walk.tile === undefined || walk.stepsLeft <= 1) return undefined;
      const next = stepFrom(walk.tile, own.direction);
      return send(next) ? next : undefined;
    };
    const direction = this.direction;
    if (direction === undefined) {
      this.trip = undefined;
      return halt();
    }
    if (walk.tile === undefined) return undefined;
    const turned = trip?.direction !== direction;
    // A click or the server took the walk over: the key walks again once it ends.
    if (!turned && own === undefined && walk.stepsLeft > 0) return undefined;
    const target = straightAhead(walk.tile, direction, isWalkable);
    if (!turned && own !== undefined) {
      // Still far from the end, or the end is an obstacle: nothing to ask.
      if (walk.stepsLeft > EXTEND_AT_STEPS) return undefined;
      if (target === undefined || same(target, own.target)) return undefined;
    }
    if (target === undefined) {
      // Turned towards an obstacle.
      this.trip = { direction, target: undefined };
      return halt();
    }
    this.trip = { direction, target: send(target) ? target : undefined };
    return this.trip.target;
  }
}

/** What the keys need to know of the predicted walk (`LocalMovement`). */
export interface WalkView {
  /** The predicted tile, where the walk turns. */
  tile: TilePosition | undefined;
  /** Tiles still to walk after it. */
  stepsLeft: number;
  /** Where the walk the player leads goes; undefined when the server leads. */
  target: TilePosition | undefined;
}

function same(a: TilePosition, b: TilePosition): boolean {
  return a.x === b.x && a.y === b.y;
}

function stepFrom(from: TilePosition, direction: WalkDirection): TilePosition {
  const step = STEPS[direction];
  return { x: from.x + step.x, y: from.y + step.y };
}

/** The farthest open tile in a straight line, up to `TRIP_TILES`; undefined against a wall. */
function straightAhead(
  from: TilePosition,
  direction: WalkDirection,
  isWalkable: (tile: TilePosition) => boolean,
): TilePosition | undefined {
  let last: TilePosition | undefined;
  let tile = from;
  for (let i = 0; i < TRIP_TILES; i += 1) {
    tile = stepFrom(tile, direction);
    if (!isWalkable(tile)) break;
    last = tile;
  }
  return last;
}
